// ============================================================================
// UNE PHRASE QUI CONTIENT DE LA MISE EN FORME — traduite ENTIÈRE, jamais en morceaux.
//
// UTILITÉ : une phrase dont un mot est en gras s'écrit naturellement en trois nœuds JSX — du texte,
// un `<strong>`, du texte. Migrée telle quelle, elle produit TROIS clés : « Les vignettes en », puis
// « pointillé », puis « sont des déclinaisons… ». Aucune n'est traduisible : le traducteur reçoit des
// bouts sans la phrase, et l'ordre des mots change d'une langue à l'autre. C'est le défaut que la
// doctrine interdit — « on ne recolle jamais des morceaux ».
//
// ⚠️ LA MISE EN FORME VOYAGE DANS LA CLÉ, SOUS FORME DE REPÈRES NUMÉROTÉS : `<1>…</1>`. Le traducteur
//    les DÉPLACE avec le mot qu'ils entourent — c'est précisément ce qu'un fragment ne permet pas. Les
//    repères sont numérotés plutôt que nommés (`<b>`) parce qu'un numéro ne présume d'aucune balise :
//    le même texte peut envelopper un `<strong>` ici et un lien ailleurs.
//
// ⚠️ LE MOTEUR RESTE SANS REACT. `@orgazone/i18n` est un paquet PUR — il servira aussi la caisse, qui
//    ne rend pas du DOM. Le rendu vit donc ici, dans le paquet de composants web.
//
//   <Trans k="Les vignettes en <1>pointillé</1> n’existent pas." parts={[(x) => <strong>{x}</strong>]} />
// ============================================================================
import { Fragment, type ReactNode } from 'react';
import { t, type Variables } from '@orgazone/i18n';

/**
 * Un repère `<n>…</n>` dans le texte traduit. Le contenu est déjà interpolé quand il arrive.
 * ⚠️ FABRIQUÉ À CHAQUE RENDU, jamais partagé : une expression régulière globale porte un CURSEUR, et
 *    un rendu interrompu en chemin (le rendu d'un repère est fourni par l'appelant, il peut lever) le
 *    laisserait au milieu du texte — la phrase suivante démarrerait amputée, une fois sur deux.
 */
const repereNeuf = () => /<(\d+)>([\s\S]*?)<\/\1>/g;

export interface TransProps {
  /** La clé — le texte français ENTIER, repères compris. */
  k: string;
  /** Les variables du texte (`{n}`, `{name}`), comme pour `t()`. */
  vars?: Variables;
  /**
   * Le rendu de chaque repère, dans l'ordre : `parts[0]` rend `<1>`, `parts[1]` rend `<2>`.
   * ⚠️ Un repère sans rendu correspondant affiche son CONTENU NU plutôt que de disparaître : perdre un
   *    mot serait pire que perdre son gras, et l'écart se voit à la relecture.
   */
  parts?: readonly ((contenu: string) => ReactNode)[];
}

/**
 * Rend une phrase traduite dont certains passages portent une mise en forme.
 * ⚠️ Le texte est traduit D'ABORD, découpé ENSUITE : c'est la traduction qui décide où sont les
 *    repères, donc l'ordre des mots de la langue d'arrivée est respecté par construction.
 */
export function Trans({ k, vars, parts = [] }: TransProps) {
  const texte = t(k, vars);
  const noeuds: ReactNode[] = [];
  let curseur = 0;
  let n = 0;
  const repere = repereNeuf();
  for (let m = repere.exec(texte); m; m = repere.exec(texte)) {
    if (m.index > curseur) noeuds.push(texte.slice(curseur, m.index));
    const rendu = parts[Number(m[1]) - 1];
    const contenu = m[2] ?? '';
    // ⚠️ UN FRAGMENT, PAS UN `<span>` : il porte la clé que React réclame sans rien ajouter au DOM.
    //    Une enveloppe de plus changerait le balisage que ce composant REMPLACE, et une règle de style
    //    écrite sur l'élément voisin cesserait de s'appliquer — sans que rien ne le signale.
    noeuds.push(rendu ? <Fragment key={`p${n}`}>{rendu(contenu)}</Fragment> : contenu);
    curseur = m.index + m[0].length;
    n += 1;
  }
  if (curseur < texte.length) noeuds.push(texte.slice(curseur));
  return <>{noeuds}</>;
}
